<template>
  <div class="generic-widget-grid-container">
    <!-- Top Action Toolbar -->
    <div v-if="showToolbar" class="mb-4">
      <!-- Edit Mode Active Sticky Banner -->
      <div v-if="isDraggable" class="customise-banner shadow-sm">
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div class="d-flex align-items-center gap-3">
            <div class="banner-pill">
              <i class="fas fa-arrows-alt me-2 text-primary"></i>
              <span class="fw-bold">{{ editorTitle }}</span>
            </div>
            <span class="text-muted small d-none d-lg-inline">
              {{ editorDescription }}
            </span>
          </div>

          <div class="d-flex align-items-center gap-2">
            <button
              v-if="allowReset && defaultLayout && defaultLayout.length > 0"
              class="btn btn-sm btn-outline-secondary btn-action-pill"
              @click="resetToDefault"
              v-tooltip.top="'Reset to default layout'"
            >
              <i class="fas fa-undo me-1"></i> Reset Default
            </button>
            <button
              v-if="allowCancel"
              class="btn btn-sm btn-outline-danger btn-action-pill"
              @click="cancelEdit"
              v-tooltip.top="'Discard changes'"
            >
              <i class="fas fa-times me-1"></i> Cancel
            </button>
            <button
              class="btn btn-sm btn-primary btn-save-pill"
              @click="saveLayout"
              v-tooltip.top="'Save and apply this layout'"
            >
              <i class="fas fa-check me-1"></i> Save Layout
            </button>
          </div>
        </div>
      </div>

      <!-- Normal Mode Controls Bar -->
      <div v-else class="d-flex justify-content-between align-items-center gap-2 flex-wrap">
        <div class="d-flex align-items-center gap-2">
          <slot name="toolbar-left"></slot>
        </div>

        <div class="d-flex align-items-center gap-2">
          <slot name="toolbar-right"></slot>

          <button
            v-if="allowCustomisation"
            class="btn-customise-layout d-none d-md-inline-flex align-items-center"
            @click="startEdit"
            v-tooltip.top="'Customise and rearrange layout widgets'"
          >
            <i class="fas fa-grip-horizontal me-2"></i>
            <span>Customise Layout</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Vue Grid Layout (Customization Mode) -->
    <grid-layout
      v-if="isDraggable"
      :layout="currentLayout"
      :col-num="colNum"
      :row-height="rowHeight"
      :margin="margin"
      :is-draggable="true"
      :is-resizable="true"
      :vertical-compact="true"
      :use-css-transforms="true"
      :autoSize="true"
      :breakpoints="{ lg: 1200, md: 997, sm: 768, xs: 480, xxs: 0 }"
    >
      <grid-item
        v-for="item in currentLayout"
        :key="item.i"
        :x="item.x"
        :y="item.y"
        :w="item.w"
        :h="item.h"
        :i="item.i"
        :min-w="item.minW || 3"
        :min-h="getMinHeight(item)"
        :is-resizable="!item.static"
        :static="item.static"
        v-show="item.showProduct !== false"
      >
        <div
          v-if="item.showProduct !== false"
          class="editor-card"
          :class="{ 'is-hidden': !item.visible }"
        >
          <!-- Card Header -->
          <div class="editor-card-header">
            <div class="d-flex align-items-center gap-2 text-truncate me-2">
              <div
                class="widget-icon-pill"
                :style="{ backgroundColor: getMeta(item).bgSoft, color: getMeta(item).color }"
              >
                <i :class="getMeta(item).icon"></i>
              </div>
              <div class="text-truncate">
                <div class="widget-title text-truncate">{{ getMeta(item).title }}</div>
                <div class="widget-subtitle text-truncate">{{ getMeta(item).subtitle }}</div>
              </div>
            </div>

            <div class="d-flex align-items-center gap-2 flex-shrink-0">
              <span class="badge-col-size">{{ item.w }} cols</span>
              <div class="drag-grip" title="Drag to move">
                <i class="fas fa-grip-vertical"></i>
              </div>
            </div>
          </div>

          <!-- Card Body / Visual Preview -->
          <div class="editor-card-body">
            <!-- Custom Named Slot per component: e.g. #preview-PieSummary -->
            <slot :name="'preview-' + item.component" :item="item" :meta="getMeta(item)">
              <!-- General Preview Slot -->
              <slot name="preview" :item="item" :meta="getMeta(item)">
                <!-- Built-in Standard Preview Mocks -->
                <template v-if="getMeta(item).previewType === 'donut' || item.component === 'PieSummary'">
                  <div class="preview-donut-wrapper">
                    <div class="mini-donut"></div>
                    <div class="mini-donut-legend">
                      <span class="legend-dot green">Completed</span>
                      <span class="legend-dot blue">In Progress</span>
                      <span class="legend-dot gray">Not Started</span>
                    </div>
                  </div>
                </template>

                <template v-else-if="getMeta(item).previewType === 'table' || item.component === 'Overdue'">
                  <div class="preview-table-wrapper">
                    <div class="mini-table-header">
                      <span>Item Name</span>
                      <span>Metrics</span>
                    </div>
                    <div class="mini-table-row" v-for="n in 3" :key="n">
                      <div class="d-flex align-items-center gap-2">
                        <div class="mini-avatar"></div>
                        <div class="mini-text-line" :style="{ width: (55 + n * 20) + 'px' }"></div>
                      </div>
                      <span class="mini-badge-count">{{ 16 - n * 4 }}</span>
                    </div>
                  </div>
                </template>

                <template v-else-if="getMeta(item).previewType === 'bars' || item.component === 'ClickThrough'">
                  <div class="preview-chart-wrapper">
                    <div class="mini-chart-bars">
                      <div class="mini-bar" style="height: 35%"></div>
                      <div class="mini-bar active" style="height: 85%"></div>
                      <div class="mini-bar" style="height: 60%"></div>
                      <div class="mini-bar" style="height: 45%"></div>
                      <div class="mini-bar active" style="height: 95%"></div>
                    </div>
                    <div class="mini-chart-caption">{{ getMeta(item).title }} Trends</div>
                  </div>
                </template>

                <template v-else-if="getMeta(item).previewType === 'security' || item.component === 'Ecc'">
                  <div class="preview-security-wrapper">
                    <div class="mini-shield-badge">
                      <i class="fas fa-shield-alt text-warning"></i>
                      <span>Active Monitoring</span>
                    </div>
                    <div class="mini-metric-grid">
                      <div class="mini-metric">
                        <span class="metric-val">0</span>
                        <span class="metric-lbl">Alerts</span>
                      </div>
                      <div class="mini-metric">
                        <span class="metric-val">100%</span>
                        <span class="metric-lbl">Healthy</span>
                      </div>
                    </div>
                  </div>
                </template>

                <template v-else-if="getMeta(item).previewType === 'compliance' || item.component === 'AcceptanceReport'">
                  <div class="preview-table-wrapper">
                    <div class="mini-table-header">
                      <span>Document / Policy</span>
                      <span>Status</span>
                    </div>
                    <div class="mini-table-row" v-for="n in 3" :key="n">
                      <div class="mini-text-line" :style="{ width: (90 + n * 15) + 'px' }"></div>
                      <span class="mini-badge-signed"><i class="fas fa-check-circle text-success me-1"></i> Completed</span>
                    </div>
                  </div>
                </template>

                <template v-else-if="getMeta(item).previewType === 'pentest' || item.component === 'PentestSummary'">
                  <div class="preview-pentest-wrapper">
                    <div class="mini-vuln-chips">
                      <span class="chip critical">0 Critical</span>
                      <span class="chip high">0 High</span>
                      <span class="chip med">2 Med</span>
                      <span class="chip low">5 Low</span>
                    </div>
                    <div class="mini-scan-status">
                      <i class="fas fa-shield-virus text-indigo me-1"></i> Scan Monitoring Active
                    </div>
                  </div>
                </template>

                <template v-else>
                  <div class="preview-generic-wrapper">
                    <i class="fas fa-cubes text-muted mb-2 fs-4"></i>
                    <span class="text-muted small">{{ getMeta(item).title }}</span>
                  </div>
                </template>
              </slot>
            </slot>
          </div>

          <!-- Card Footer Controls -->
          <div class="editor-card-footer" @click.stop>
            <div class="d-flex align-items-center justify-content-between w-100">
              <div class="visibility-status d-flex align-items-center gap-2">
                <i :class="item.visible ? 'fas fa-eye text-primary' : 'fas fa-eye-slash text-muted'"></i>
                <span class="small" :class="item.visible ? 'fw-bold text-dark' : 'text-muted'">
                  {{ item.visible ? 'Visible on Dashboard' : 'Hidden from View' }}
                </span>
              </div>

              <label class="modern-switch" :title="item.visible ? 'Hide Widget' : 'Show Widget'">
                <input
                  type="checkbox"
                  v-model="item.visible"
                  :disabled="item.showProduct === false"
                />
                <span class="modern-slider round"></span>
              </label>
            </div>
          </div>
        </div>
      </grid-item>
    </grid-layout>

    <!-- Normal Display Mode (Bootstrap Grid) -->
    <div v-else>
      <div class="row align-items-start">
        <div
          v-for="(item, index) in sortedLayout"
          :key="item.i || index"
          :class="getBootstrapClass(item)"
          class="mt-4 mb-3"
          v-show="item.visible !== false && item.showProduct !== false"
        >
          <!-- Default Live Slot for Component Rendering -->
          <slot :item="item" :index="index" :meta="getMeta(item)">
            <div class="card p-4 shadow-sm border-0">
              <h5>{{ getMeta(item).title }}</h5>
              <p class="text-muted">{{ getMeta(item).subtitle }}</p>
            </div>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GenericWidgetGrid',

  props: {
    modelValue: {
      type: Array,
      default: () => []
    },
    defaultLayout: {
      type: Array,
      default: () => []
    },
    widgets: {
      type: [Object, Array],
      default: () => ({})
    },
    showToolbar: {
      type: Boolean,
      default: true
    },
    allowCustomisation: {
      type: Boolean,
      default: true
    },
    allowReset: {
      type: Boolean,
      default: true
    },
    allowCancel: {
      type: Boolean,
      default: true
    },
    colNum: {
      type: Number,
      default: 12
    },
    rowHeight: {
      type: Number,
      default: 30
    },
    margin: {
      type: Array,
      default: () => [16, 16]
    },
    editorTitle: {
      type: String,
      default: 'Layout Editor'
    },
    editorDescription: {
      type: String,
      default: 'Drag cards to rearrange, use bottom-right handles to resize, and toggle visibility.'
    }
  },

  emits: ['update:modelValue', 'save', 'cancel', 'reset', 'edit-start', 'edit-end'],

  data() {
    return {
      isDraggable: false,
      currentLayout: [],
      savedLayout: []
    };
  },

  watch: {
    modelValue: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (!this.isDraggable) {
          this.currentLayout = this.deepClone(newVal);
          this.savedLayout = this.deepClone(newVal);
        }
      }
    }
  },

  computed: {
    sortedLayout() {
      return [...this.currentLayout].sort((a, b) => {
        if (a.y === b.y) return (a.x || 0) - (b.x || 0);
        return (a.y || 0) - (b.y || 0);
      });
    }
  },

  methods: {
    startEdit() {
      this.savedLayout = this.deepClone(this.currentLayout);
      this.isDraggable = true;
      this.$emit('edit-start');
    },

    saveLayout() {
      this.isDraggable = false;
      this.savedLayout = this.deepClone(this.currentLayout);
      this.$emit('update:modelValue', this.currentLayout);
      this.$emit('save', this.currentLayout);
      this.$emit('edit-end');
    },

    cancelEdit() {
      this.currentLayout = this.deepClone(this.savedLayout);
      this.isDraggable = false;
      this.$emit('cancel');
      this.$emit('edit-end');
    },

    resetToDefault() {
      if (!this.defaultLayout || this.defaultLayout.length === 0) return;
      this.currentLayout = this.deepClone(this.defaultLayout);
      this.$emit('reset', this.currentLayout);
      if (this.$toast) {
        this.$toast.info('Layout reset to default. Click Save Layout to apply.');
      }
    },

    getMeta(item) {
      if (!item) return this.getDefaultMeta('Widget');
      const key = item.component || item.id || item.i;

      if (this.widgets && typeof this.widgets === 'object') {
        if (Array.isArray(this.widgets)) {
          const found = this.widgets.find(w => w.id === key || w.component === key);
          if (found) return { ...this.getDefaultMeta(key), ...found };
        } else if (this.widgets[key]) {
          return { ...this.getDefaultMeta(key), ...this.widgets[key] };
        }
      }

      return this.getDefaultMeta(key);
    },

    getDefaultMeta(name) {
      return {
        title: name || 'Widget',
        subtitle: 'Dashboard Widget',
        icon: 'fas fa-th-large',
        color: '#00a0c1',
        bgSoft: '#e0f7fa',
        previewType: 'generic'
      };
    },

    getMinHeight(item) {
      if (item.minH) return item.minH;
      const meta = this.getMeta(item);
      if (meta.minH) return meta.minH;
      return 6;
    },

    getBootstrapClass(item) {
      const colSize = Math.floor(((item.w || 6) / 12) * 12);
      if (typeof window !== 'undefined' && window.innerWidth < 1650) {
        return `col-12 col-sm-${Math.min(colSize * 2, 12)} col-md-${Math.min(colSize * 2, 12)} col-lg-6`.trim();
      }
      return `col-12 col-sm-${Math.min(colSize * 2, 12)} col-md-${Math.min(colSize, 6)} col-lg-${colSize}`.trim();
    },

    deepClone(obj) {
      if (obj === null || typeof obj !== 'object') return obj;
      if (obj instanceof Date) return new Date(obj.getTime());
      if (Array.isArray(obj)) {
        return obj.map(item => this.deepClone(item));
      }
      const copy = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          copy[key] = this.deepClone(obj[key]);
        }
      }
      return copy;
    }
  }
};
</script>

<style scoped>
/* Grid Layout & Container */
:deep(.vue-grid-layout) {
  position: relative;
  transition: height 200ms ease;
}

:deep(.vue-grid-item) {
  touch-action: none;
  background: transparent;
  border: none;
}

:deep(.vue-grid-item.vue-grid-placeholder) {
  background: rgba(0, 160, 193, 0.12) !important;
  border: 2px dashed #00a0c1 !important;
  border-radius: 14px !important;
  opacity: 1 !important;
  z-index: 2;
  transition-duration: 100ms;
}

:deep(.vue-grid-item > .vue-resizable-handle) {
  position: absolute;
  width: 24px;
  height: 24px;
  bottom: 4px;
  right: 4px;
  cursor: se-resize;
  z-index: 20 !important;
  background: none;
}

:deep(.vue-grid-item > .vue-resizable-handle::after) {
  content: '';
  position: absolute;
  right: 4px;
  bottom: 4px;
  width: 10px;
  height: 10px;
  border-right: 2.5px solid #00a0c1;
  border-bottom: 2.5px solid #00a0c1;
  border-bottom-right-radius: 3px;
  transition: transform 0.2s ease, border-color 0.2s ease;
}

:deep(.vue-grid-item > .vue-resizable-handle:hover::after) {
  transform: scale(1.2);
  border-color: #007f99;
}

/* Customization Top Banner */
.customise-banner {
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-left: 4px solid #00a0c1;
  border-radius: 12px;
  padding: 12px 18px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.05);
}

.banner-pill {
  display: inline-flex;
  align-items: center;
  background: #e0f7fa;
  color: #008ca9;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
}

.btn-action-pill {
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.825rem;
  padding: 6px 14px;
  transition: all 0.2s ease;
}

.btn-action-pill:hover {
  transform: translateY(-1px);
}

.btn-save-pill {
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.825rem;
  padding: 6px 16px;
  background-color: #00a0c1;
  border-color: #00a0c1;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(0, 160, 193, 0.25);
  transition: all 0.2s ease;
}

.btn-save-pill:hover {
  background-color: #008ca9;
  border-color: #008ca9;
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(0, 160, 193, 0.35);
}

.btn-customise-layout {
  display: inline-flex;
  align-items: center;
  background: #ffffff;
  color: #00a0c1;
  border: 1.5px solid #d0ebf2;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  padding: 7px 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-customise-layout:hover {
  background: #f0fdfa;
  border-color: #00a0c1;
  color: #007f99;
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(0, 160, 193, 0.15);
}

/* Editor Card in Grid */
.editor-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.04);
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
  user-select: none;
  position: relative;
}

.editor-card:hover {
  border-color: #00a0c1;
  box-shadow: 0 8px 20px rgba(0, 160, 193, 0.12);
}

.editor-card.is-hidden {
  opacity: 0.55;
  background: #f8fafc;
  border-style: dashed;
  border-color: #cbd5e1;
}

.editor-card-header {
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #f1f5f9;
  background: #ffffff;
  flex-shrink: 0;
}

.widget-icon-pill {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  flex-shrink: 0;
}

.widget-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
}

.widget-subtitle {
  font-size: 0.72rem;
  color: #64748b;
  line-height: 1.2;
}

.badge-col-size {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 10px;
  background: #f1f5f9;
  color: #64748b;
}

.drag-grip {
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  border-radius: 6px;
  cursor: grab;
  transition: background 0.2s, color 0.2s;
}

.drag-grip:hover {
  color: #00a0c1;
  background: #e0f7fa;
}

.editor-card-body {
  flex: 1;
  min-height: 85px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafcff;
  overflow: hidden;
  position: relative;
}

.editor-card-footer {
  padding: 8px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #f1f5f9;
  background: #ffffff;
  flex-shrink: 0;
}

/* Visual Mocks for Cards */
/* Pie/Donut Mock */
.preview-donut-wrapper {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  justify-content: center;
  min-width: 0;
}

.mini-donut {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 7px solid #00a0c1;
  border-top-color: #10b981;
  border-left-color: #e2e8f0;
  box-sizing: border-box;
  flex-shrink: 0;
}

.mini-donut-legend {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
}

.legend-dot {
  font-size: 0.72rem;
  line-height: 1.4;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.legend-dot::before {
  content: '';
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.legend-dot.green::before {
  background-color: #10b981;
}

.legend-dot.blue::before {
  background-color: #00a0c1;
}

.legend-dot.gray::before {
  background-color: #cbd5e1;
}

/* Table Skeleton Mock */
.preview-table-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.mini-table-header {
  display: flex;
  justify-content: space-between;
  font-size: 0.7rem;
  font-weight: 700;
  color: #64748b;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 3px;
}

.mini-table-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px 0;
}

.mini-avatar {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: #e2e8f0;
}

.mini-text-line {
  height: 7px;
  background-color: #e2e8f0;
  border-radius: 4px;
}

.mini-badge-count {
  font-size: 0.68rem;
  font-weight: 700;
  background-color: #fee2e2;
  color: #dc2626;
  padding: 1px 6px;
  border-radius: 8px;
}

.mini-badge-signed {
  font-size: 0.68rem;
  font-weight: 600;
  color: #059669;
}

/* Phishing Bar Chart Mock */
.preview-chart-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.mini-chart-bars {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 48px;
  width: 80%;
  justify-content: center;
  border-bottom: 1.5px solid #e2e8f0;
  padding-bottom: 2px;
}

.mini-bar {
  width: 14px;
  background-color: #ddd6fe;
  border-top-left-radius: 3px;
  border-top-right-radius: 3px;
  transition: height 0.3s ease;
}

.mini-bar.active {
  background-color: #8b5cf6;
}

.mini-chart-caption {
  font-size: 0.7rem;
  color: #64748b;
  font-weight: 500;
}

/* ECC Security Mock */
.preview-security-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
}

.mini-shield-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background-color: #fef3c7;
  color: #b45309;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 12px;
}

.mini-metric-grid {
  display: flex;
  gap: 16px;
}

.mini-metric {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.metric-val {
  font-size: 0.9rem;
  font-weight: 800;
  color: #1e293b;
}

.metric-lbl {
  font-size: 0.68rem;
  color: #64748b;
}

/* Pentest Mock */
.preview-pentest-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
}

.mini-vuln-chips {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  justify-content: center;
}

.chip {
  font-size: 0.65rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 8px;
}

.chip.critical {
  background-color: #fee2e2;
  color: #b91c1c;
}

.chip.high {
  background-color: #ffedd5;
  color: #c2410c;
}

.chip.med {
  background-color: #fef9c3;
  color: #a16207;
}

.chip.low {
  background-color: #f1f5f9;
  color: #475569;
}

.mini-scan-status {
  font-size: 0.7rem;
  color: #4f46e5;
  font-weight: 600;
}

.preview-generic-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* Modern iOS-style Switch */
.modern-switch {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  margin: 0;
}

.modern-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.modern-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e1;
  transition: 0.25s ease;
  border-radius: 20px;
}

.modern-slider:before {
  position: absolute;
  content: "";
  height: 14px;
  width: 14px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.25s ease;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.modern-switch input:checked + .modern-slider {
  background-color: #00a0c1;
}

.modern-switch input:checked + .modern-slider:before {
  transform: translateX(16px);
}
</style>
